import styled from '@emotion/styled';
import {
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
} from '../../../../style-utils/theme';
import { down, touch } from '../../../../style-utils/media-queries';
import { Body } from '../../visitor-exit/visitor-exit-page.styled';

/**
 * EXPECTED TODAY — only what the exit desk's kit does not already have.
 *
 * The screen IS the exit-desk template (UI-CONTRACT §5): the card, the tiles,
 * the search, the rows, the inline confirm and the banners are imported from
 * visitor-exit-page.styled.tsx, not copied. What lives here is the handful of
 * parts the one-tap sign-in needs and a sign-out does not: the party stepper and
 * the note inside the confirm, the outside-the-window tick, the expandable note,
 * and a slot that lets a disabled action carry its reason.
 *
 * Tokens only. NO BACKTICKS IN ANY COMMENT BELOW: they sit inside styled
 * template literals, where one silently ends the string.
 */

/* The exit desk's page column, plus the room the IQ launcher needs at the end
   of a scrolling desk page (layout.fabReserve), so the last row's action is
   never under it. */
export const ExpectedBody = styled(Body)`
  padding-bottom: ${layout.fabReserve};
`;

/* The zone line under the header, shown only when the tablet is not on the
   building's clock. */
export const ZoneNote = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* The row's action cell. On a phone the row stacks and the actions take the
   full width, like the exit desk's own. */
export const ActionSlot = styled.div`
  min-width: 0;

  ${down('sm')} {
    flex: 1 1 auto;
  }
`;

export const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};

  ${down('sm')} {
    width: 100%;
  }
`;

/* The second meta line and the pill sit under the name; the note toggle and
   its text follow them. */
export const MetaStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.half};
  min-width: 0;
`;

/* "Show note" — a text button, underlined lagoon like the product's other
   inline actions, 44px tall on touch. */
export const NoteToggle = styled.button`
  -webkit-appearance: none;
  appearance: none;
  padding: 0;
  border: none;
  background: none;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  text-decoration: underline;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.irisDeep};
  }

  &:focus-visible {
    outline: none;
    border-radius: ${radius.xs};
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const NoteText = styled.p`
  margin: 0;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

/* The confirm panel's body: the sentence, the fields, the tick and the button,
   one column across the panel's full width. It is also the focus target when
   the panel opens (tabIndex -1), so it draws the focus ring itself. */
export const ConfirmStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  flex: 1 1 100%;
  min-width: 0;
  border-radius: ${radius.xs};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: ${shadow.focus};
  }
`;

/* Party size and the note side by side on a desk, stacked on a phone. */
export const ConfirmFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.sm};
`;

export const FieldBlock = styled.div<{ $grow?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  flex: ${({ $grow }) => ($grow ? '1 1 240px' : '0 0 auto')};
`;

export const FieldLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

/* The explicit "sign in anyway" line for an early or late arrival. The whole
   row is the label, so the tap target is the sentence, not a 24px box. */
export const TickRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.warning};
  border-radius: ${radius.xs};
  background: ${palette.warningSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  cursor: pointer;

  input {
    flex: none;
    width: ${spacing.md};
    height: ${spacing.md};
    margin: 0;
    accent-color: ${palette.lagoon};
    cursor: pointer;
  }
`;

export const ConfirmButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};

  ${down('sm')} {
    flex-direction: column;
  }
`;

/* The PASS_NOT_TODAY answer: what happened, and the one way forward. */
export const NotTodayText = styled.span`
  display: block;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
`;

export const InlineError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.half};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.alertSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
`;

/* The list card's title row: "Today's list" and, where the building lets this
   desk add passes (migration 118), "Add expected visitor" beside it. Wraps
   under a narrow phone rather than squeezing the title. */
export const ListHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

/* THE ROW NAME OPENS THE PASS (resident-portal Plan 3, RP-3.5). A button that
   reads exactly like the name it replaces — same face, size and ink — and
   shows it can be pressed by an underline on hover and the focus ring on the
   keyboard. The whole name is the target; on touch it grows to the 44px floor
   without moving the text. */
export const RowNameButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  max-width: 100%;
  /* 24px under a mouse (the scanner's WCAG 2.5.8 floor), 44px on touch below. */
  min-height: ${spacing.md};
  margin: 0;
  padding: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    text-decoration: underline;
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

/* A SCANNED PASS (Plan 6, RP-6.4): the name on the pass, large, at the top of
   the confirm — so the desk compares it with the person standing there before
   the one tap. A caption says where the name came from. */
export const ScannedPass = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  padding: ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.irisTintSoft};
  border: 1px solid ${palette.line};
`;

export const ScannedCaption = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

export const ScannedName = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;
